import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { bondsInsurancePage, companyInfo } from '../mock';
import { CheckCircle2, Phone, MessageCircle, Shield } from 'lucide-react';

const BondsInsurance = () => {
  const { language } = useLanguage();
  const content = bondsInsurancePage[language];

  const whatsappUrl = `https://wa.me/${companyInfo.phoneWhatsApp}?text=${encodeURIComponent(content.whatsappMessage)}`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-indigo-700 to-indigo-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-white/10 p-4 rounded-full mb-6">
              <Shield className="w-16 h-16" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              {content.title}
            </h1>
            <p className="text-xl md:text-2xl text-indigo-100 mb-8">
              {content.subtitle}
            </p>
            <p className="text-lg text-indigo-50">
              {content.description}
            </p>
          </div>
        </div>
      </section>

      {/* Coverage Examples */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
              {language === 'en' ? 'Types of Bonds' : 'Tipos de Fianzas'}
            </h2>
            <div className="grid gap-4">
              {content.coverageExamples.map((example, index) => (
                <div key={index} className="flex items-start gap-3 bg-gray-50 p-4 rounded-lg">
                  <CheckCircle2 className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-1" />
                  <p className="text-lg text-gray-700">{example}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What to Prepare */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
              {content.whatToPrepare.title}
            </h2>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg mb-8">
              <ul className="space-y-3">
                {content.whatToPrepare.items.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="text-blue-600 font-bold">•</span>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-indigo-700 to-indigo-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {language === 'en' ? 'Get Your Bond Quote Today' : 'Obtenga Su Cotización de Fianza Hoy'}
            </h2>
            <p className="text-xl text-indigo-100 mb-8">
              {language === 'en' 
                ? 'Contact us for professional bonding solutions' 
                : 'Contáctenos para soluciones profesionales de fianzas'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-white text-indigo-700 px-8 py-4 rounded-full text-lg font-semibold hover:bg-indigo-50 transition-all shadow-lg"
              >
                <MessageCircle className="w-6 h-6" />
                {language === 'en' ? 'Quote via WhatsApp' : 'Cotizar por WhatsApp'}
              </a>
              <a
                href={`tel:${companyInfo.phone}`}
                className="inline-flex items-center justify-center gap-2 bg-indigo-800 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-indigo-900 transition-all shadow-lg"
              >
                <Phone className="w-6 h-6" />
                {companyInfo.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BondsInsurance;